import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AppShell } from "../components/AppShell";
import { CompanyLogo } from "../components/CompanyLogo";
import { DivisionChips } from "../components/DivisionChips";
import { Card, ErrorBox, STATUS_BG, STATUS_TEXT, Skeleton, StatusDot } from "../components/ui";
import { usePortfolio, useSourcingEvents, type PortfolioRow } from "../lib/api";
import { fmtNumber, fmtValue, localeFor, type Status } from "../lib/format";
import { barGeometry, countByHealth, monthTicks, sortByHealth, timelineRange, totalBudget } from "../lib/projects";

const PRIORITY_CLS: Record<string, string> = {
  High: "bg-red-50 text-status-red border-status-red/40",
  Urgent: "bg-red-50 text-status-red border-status-red/40",
  Medium: "bg-amber-50 text-status-amber border-status-amber/40",
  Low: "bg-brand-grey-light text-brand-charcoal border-brand-grey-light",
};

/** Projects experience preview (WP-09, PRJ-01 … PRJ-03): the Demo Packaging factory pipeline with deterministic schedule health. */
export default function Projects() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [company, setCompany] = useState("");
  const all = usePortfolio();
  const sourcing = useSourcingEvents();
  const projects = useMemo(() => all.data?.projects ?? [], [all.data]);
  const companies = useMemo(() => [...new Set(projects.map((p) => p.company).filter(Boolean))].sort(), [projects]);
  const shown = useMemo(() => sortByHealth(projects.filter((p) => !company || p.company === company)), [projects, company]);
  const counts = countByHealth(shown);
  const range = useMemo(() => timelineRange(shown), [shown]);
  const today = new Date();
  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s}T00:00:00`)) : "–");

  return (
    <AppShell subtitle={t("tiles.projects.title")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mt-1 font-heading text-2xl font-semibold">{t("proj.title")}</h1>
          <p className="text-sm text-brand-grey">{t("proj.subtitle")}</p>
        </div>
        {all.data && <div className="text-xs text-brand-grey">{t("proj.asOf")} {fmtDate(all.data.as_of)}</div>}
      </div>

      {companies.length > 1 && (
        <div className="mt-4"><DivisionChips companies={companies} value={company} onChange={setCompany} allLabel={t("exec.allDivisions")} /></div>
      )}

      {all.isError && <div className="mt-6"><ErrorBox error={all.error} onRetry={() => void all.refetch()} /></div>}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {(["Red", "Amber", "Green"] as Status[]).map((s) => (
          <Card key={s} className="flex items-center gap-3 px-4 py-3">
            <StatusDot status={s} className="h-3 w-3" />
            <div>
              <div className={`font-heading text-2xl font-semibold ${STATUS_TEXT[s]}`}>{all.isLoading ? "…" : counts[s]}</div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`proj.health.${s}`)}</div>
            </div>
          </Card>
        ))}
        <Card className="px-4 py-3">
          <div className="font-heading text-2xl font-semibold text-brand-charcoal">{all.isLoading ? "…" : shown.length}</div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("proj.count")}</div>
        </Card>
        <Card className="px-4 py-3">
          <div className="font-heading text-xl font-semibold text-brand-charcoal">{all.isLoading ? "…" : fmtValue(totalBudget(shown), "CDF", lng)}</div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("proj.total")}</div>
        </Card>
      </div>

      {/* sourcing entry (SRC-01 … SRC-12) */}
      <Card className="mt-6 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <div className="font-heading text-base font-semibold">{t("src.title")}</div>
          <div className="text-xs text-brand-grey">{t("src.entryHint")}</div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {sourcing.data && <span className="text-brand-grey">{t("src.eventsCount", { count: sourcing.data.count })}</span>}
          <Link to="/procure" className="rounded border border-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary hover:bg-brand-primary/5" data-procure-link>{t("p2p.link")}</Link>
          <Link to="/projects/sourcing" className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-primary/90">{t("src.open")}</Link>
        </div>
      </Card>

      {/* portfolio table */}
      <Card className="mt-6 overflow-hidden">
        {all.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : shown.length === 0 ? (
          <div className="p-8 text-center text-sm text-brand-grey">{t("proj.noProjects")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("proj.health.label")}</th>
                  <th className="px-4 py-2">{t("proj.project")}</th>
                  <th className="px-4 py-2">{t("proj.priority")}</th>
                  <th className="px-4 py-2 w-64">{t("proj.progress")}</th>
                  <th className="px-4 py-2">{t("proj.start")}</th>
                  <th className="px-4 py-2">{t("proj.end")}</th>
                  <th className="px-4 py-2">{t("prj.forecast")}</th>
                  <th className="px-4 py-2 text-right">{t("proj.budget")}</th>
                  <th className="px-4 py-2">{t("prj.controls")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => <ProjectRow key={p.name} p={p} lng={lng} fmtDate={fmtDate} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* timeline */}
      {range && shown.length > 0 && (
        <Card className="mt-6 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-base font-semibold">{t("proj.timeline")}</h2>
            <span className="flex items-center gap-1 text-xs text-brand-grey"><span className="inline-block h-3 w-0.5 bg-brand-red" /> {t("proj.today")}</span>
          </div>
          <div className="relative ml-56 h-5 border-b border-brand-grey-light text-[10px] text-brand-grey">
            {monthTicks(range).map((tick) => (
              <span key={tick.date.toISOString()} className="absolute -translate-x-1/2" style={{ left: `${tick.left}%` }}>
                {new Intl.DateTimeFormat(localeFor(lng), { month: "short", year: "2-digit", timeZone: "UTC" }).format(tick.date)}
              </span>
            ))}
          </div>
          <div className="space-y-2 pt-2">
            {shown.map((p) => {
              const g = barGeometry(p, range, today);
              return (
                <div key={p.name} className="flex items-center gap-3">
                  <div className="w-56 shrink-0 truncate text-xs" title={p.project_name}>
                    <CompanyLogo company={p.company} size={12} className="mr-1 align-middle" />{p.project_name}
                  </div>
                  <div className="relative h-5 flex-1 rounded bg-[#F6F8FA]">
                    {g.valid && (
                      <div className={`absolute top-1 h-3 rounded ${STATUS_BG[p.schedule_status]} opacity-80`} style={{ left: `${g.left}%`, width: `${g.width}%` }}
                        title={`${fmtNumber(p.percent_complete, lng, 0)} %`}>
                        <div className="h-full rounded bg-white/50" style={{ width: `${Math.max(0, 100 - (p.percent_complete ?? 0))}%`, marginLeft: `${p.percent_complete ?? 0}%` }} />
                      </div>
                    )}
                    <div className="absolute top-0 h-5 w-0.5 bg-brand-red" style={{ left: `${g.today}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </AppShell>
  );
}

function ProjectRow({ p, lng, fmtDate }: { p: PortfolioRow; lng: string; fmtDate: (s: string | null | undefined) => string }) {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, p.percent_complete ?? 0));
  const expected = p.schedule_expected == null ? null : Math.max(0, Math.min(100, p.schedule_expected));
  const daysLeft = p.days_left ?? null;
  return (
    <tr className="border-t border-brand-grey-light align-top hover:bg-[#F6F8FA]">
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-semibold text-white ${STATUS_BG[p.schedule_status]}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-white" />{t(`proj.health.${p.schedule_status}`)}
        </span>
      </td>
      <td className="px-4 py-3">
        <Link to={`/projects/${encodeURIComponent(p.name)}`} className="font-semibold text-brand-primary hover:underline">{p.project_name}</Link>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-brand-grey"><CompanyLogo company={p.company} size={14} />{p.company}</div>
        {p.notes && <div className="mt-1 max-w-md text-xs text-brand-grey">{p.notes.replace(/<[^>]+>/g, "")}</div>}
      </td>
      <td className="px-4 py-3">
        {p.priority && <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${PRIORITY_CLS[p.priority] ?? PRIORITY_CLS.Low}`}>{t(`priority.${p.priority}`, p.priority)}</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold">{fmtNumber(pct, lng, 0)} %</span>
          {expected != null && <span className="text-brand-grey">{t("proj.expected")} {fmtNumber(expected, lng, 0)} %</span>}
        </div>
        <div className="relative mt-1 h-2 rounded bg-brand-grey-light">
          <div className={`h-2 rounded ${STATUS_BG[p.schedule_status]}`} style={{ width: `${pct}%` }} />
          {expected != null && <div className="absolute -top-0.5 h-3 w-0.5 bg-brand-charcoal" style={{ left: `${expected}%` }} title={t("proj.expected")} />}
        </div>
        {daysLeft != null && (
          <div className={`mt-1 text-[11px] ${daysLeft < 0 ? "text-status-red" : "text-brand-grey"}`}>
            {daysLeft < 0 ? t("proj.overdue", { n: -daysLeft }) : t("proj.daysLeft", { n: daysLeft })}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs">{fmtDate(p.expected_start_date)}</td>
      <td className="px-4 py-3 text-xs">{fmtDate(p.expected_end_date)}</td>
      <td className="px-4 py-3 text-xs">{fmtDate(p.schedule.forecast_end)}{p.schedule.variance_days !== 0 && <div className={`text-[11px] font-semibold ${p.schedule.variance_days > 0 ? "text-status-red" : "text-status-green"}`}>{p.schedule.variance_days > 0 ? "+" : ""}{p.schedule.variance_days} {t("prj.days")} · {p.critical_count} {t("prj.detail.tiles.critical").toLowerCase()}</div>}</td>
      <td className="px-4 py-3 text-right text-xs font-semibold">{fmtValue(p.estimated_costing, "CDF", lng)}{p.cost.cost_variance_pct != null && <div className={`text-[11px] ${p.cost.cost_variance < 0 ? "text-status-red" : "text-brand-grey"}`}>{t("prj.costVariance")} {p.cost.cost_variance_pct > 0 ? "+" : ""}{fmtNumber(p.cost.cost_variance_pct, lng, 1)} %</div>}</td>
      <td className="px-4 py-3 text-xs">
        <div className="flex flex-wrap gap-1">
          {p.open_delays > 0 && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-status-amber">{p.open_delays} {t("prj.detail.tiles.delays").toLowerCase()}</span>}
          {p.open_decisions > 0 && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-brand-primary">{p.open_decisions} RFI</span>}
          {p.late_deliverables > 0 && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-status-red">{p.late_deliverables} {t("prj.detail.deliverables.late")}</span>}
          {p.claimed_milestones > 0 && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-status-amber">{p.claimed_milestones} {t("prj.detail.milestones.statuses.Claimed").toLowerCase()}</span>}
          {p.procurement_delayed > 0 && <span className="rounded bg-brand-grey-light px-1.5 py-0.5 text-[11px] font-semibold">{p.procurement_delayed} {t("prj.detail.tiles.procurement").toLowerCase()}</span>}
          <Link to={`/projects/${encodeURIComponent(p.name)}`} className="text-brand-primary hover:underline">{t("prj.open")} →</Link>
        </div>
      </td>
    </tr>
  );
}
